import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Divider,
} from '@mui/material';
import { Menu } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { hasRoles } from '../components/utils';
import { ReactComponent as TMJLogo } from '../assets/T.M.J._Logo.svg';
import {
  Board24Filled,
  PersonKey20Filled,
  HatGraduation24Filled,
  People24Filled,
  Book24Filled,
  Database24Filled,
  QuestionCircle24Filled,
  BookLetter24Filled,
} from '@fluentui/react-icons';
import { useSidebarContext } from '../context/SidebarContext';

const Sidebar = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTutor, setIsTutor] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const location = useLocation();

  const { isSidebarOpen, setIsSidebarOpen } = useSidebarContext();


  useEffect(() => {
    try {
      setIsAdmin(hasRoles('Admin'));
      setIsTutor(hasRoles('Tutor'));
      setIsOwner(hasRoles('Owner'));
    } catch (error) {
      console.error('Error fetching user roles:', error);
    }
  }, []);

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <Board24Filled />,
      path: '/',
      roles: ['Tutor', 'Admin', 'Owner'],
    },
    {
      text: 'Management', //Admins for manage user
      icon: (
        <PersonKey20Filled
          style={{ transform: 'scale(1.25)', display: 'inline-block' }}
        />
      ),
      path: '/manageDatabase',
      roles: ['Owner'],
    },
    {
      text: 'Tutors',
      icon: <People24Filled />,
      path: '/tutorAdmin',
      roles: ['Admin', 'Owner'],
    },
    {
      text: 'Students',
      icon: <HatGraduation24Filled />,
      path: '/studentAdmin',
      roles: ['Admin', 'Owner'],
    },
    {
      text: 'My Students',
      icon: <HatGraduation24Filled />,
      path: '/myStudents',
      roles: ['Tutor'],
    },
    {
      text: 'Logs',
      icon: <Book24Filled />,
      path: '/sessions',
      roles: ['Tutor', 'Admin', 'Owner'],
    },
    // {
    //   text: 'Documentation',
    //   icon: <BookLetter24Filled />,
    //   path: '/info',
    //   roles: ['Tutor', 'Admin', 'Owner'],
    // },
    // {
    //   text: 'Database',
    //   icon: <Database24Filled />,
    //   path: '/manageDatabase',
    //   roles: ['Owner'],
    // },
  ];

  // Check if a menu item should be displayed
  const isMenuVisible = (roles) => {
    return roles.some(
      (role) =>
        (role === 'Owner' && isOwner) ||
        (role === 'Admin' && isAdmin) ||
        (role === 'Tutor' && isTutor)
    );
  };

  return (
    <Drawer
      variant="permanent"
      open={isSidebarOpen}
      sx={{
        width: isSidebarOpen ? 220 : 80,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isSidebarOpen ? 220 : 80,
          transition: 'width 0.3s',
          backgroundColor: 'var(--navyBlue-default)',
          color: 'white',
          overflowX: 'hidden',
          padding: '1rem',
          gap: '1.25rem',
          boxSizing: 'border-box',
        },
      }}
    >
      {/* Sidebar Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isSidebarOpen ? 'space-between' : 'center',
          marginTop: '0.2rem'
        }}
      >
        {isSidebarOpen && <TMJLogo />}
        <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)} sx={{ color: 'white' }}>
          <Menu />
        </IconButton>
      </Box>

      {/* Menu Items */}
      <List>
        {menuItems
          .filter((item) => isMenuVisible(item.roles)) // Show allowed menus based on role
          .map(({ text, icon, path }) => {
            const isActive = location.pathname === path; // Check if menu is active

            return (
              <ListItem key={text} disablePadding sx={{ mb: 1.5 }}>
                <ListItemButton
                  component={Link}
                  to={path}
                  sx={{
                    justifyContent: isSidebarOpen ? 'flex-start' : 'center',
                    px: 0.8,
                    borderRadius: 2,
                    backgroundColor: isActive
                      ? 'var(--navyBlue-light)'
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: 'var(--navyBlue-light)',
                    },
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'white',
                      minWidth: 40,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {icon}
                  </ListItemIcon>
                  {isSidebarOpen && (
                    <ListItemText
                      primary={text}
                      primaryTypographyProps={{
                        sx: { fontWeight: isActive ? '700' : '500' },
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
      </List>

      <Divider sx={{ mt: 'auto' }} />

      {/* Help Section */}
      <ListItem disablePadding>
        <ListItemButton
          component={Link}
    to="/info"
          sx={{ justifyContent: isSidebarOpen ? 'flex-start' : 'center', px: 2, borderRadius: 2, backgroundColor: location.pathname === "/info" ? "var(--navyBlue-light)" : "transparent",
            '&:hover': {
              backgroundColor: 'var(--navyBlue-light)',
            }, }}
        >
          <ListItemIcon
            sx={{
              color: 'white',
              minWidth: 40,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <BookLetter24Filled />
          </ListItemIcon>
          {isSidebarOpen && <ListItemText primary="Documentation" primaryTypographyProps={{
          sx: { fontWeight: location.pathname === "/info" ? '700' : '500' },
        }}/>}
        </ListItemButton>
      </ListItem>
    </Drawer>
  );
};

export default Sidebar;
